"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/trpc/react";
import { toast } from "sonner";

interface Organization {
  clerkOrgId: string;
  name: string;
  role: string;
}

interface OrganizationSelectorProps {
  organizations: Organization[];
  open: boolean;
  onClose: () => void;
}

export function OrganizationSelector({
  organizations,
  open,
  onClose,
}: OrganizationSelectorProps) {
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");

  const setActiveOrgMutation =
    api.organizations.setActiveOrganization.useMutation({
      onSuccess: () => {
        toast.success("Organization selected successfully!");
        onClose();
        // Redirect to dashboard for fresh session
        window.location.href = "/dashboard";
      },
      onError: (error) => {
        console.error("Failed to set active organization:", error);
        toast.error("Failed to select organization. Please try again.");
      },
    });

  const handleSelect = () => {
    if (!selectedOrgId) {
      toast.error("Please select an organization");
      return;
    }

    setActiveOrgMutation.mutate({ clerkOrgId: selectedOrgId });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => !setActiveOrgMutation.isPending && onClose()}
    >
      <DialogContent
        className="sm:max-w-[500px]"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Select Your Organization</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <p className="text-center text-muted-foreground">
            You belong to multiple organizations. Which one would you like to
            use?
          </p>

          <div className="space-y-3">
            {organizations.map((org) => (
              <div
                key={org.clerkOrgId}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedOrgId === org.clerkOrgId
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-muted/50"
                }`}
                onClick={() => setSelectedOrgId(org.clerkOrgId)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedOrgId === org.clerkOrgId
                        ? "border-primary bg-primary"
                        : "border-muted-foreground"
                    }`}
                  >
                    {selectedOrgId === org.clerkOrgId && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold">{org.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {org.role === "org:admin" ? "Admin" : "Member"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center pt-2">
            <Button
              onClick={handleSelect}
              disabled={!selectedOrgId || setActiveOrgMutation.isPending}
              size="lg"
              className="min-w-[120px]"
            >
              {setActiveOrgMutation.isPending ? "Selecting..." : "Continue"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
